import { DEFAULT_PIXEL_CONFIG } from './defaults/pixel-default-config';
import { PixelConfigurationError } from './errors';
import { normalizePixelId } from './pixelId';
import type { PixelConfig } from './types/pixel';
import type { PixelSettings } from './types/pixel-settings';

const BLOCKED_KEYS = new Set(['__proto__', 'prototype', 'constructor']);

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  if (Array.isArray(value)) {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function cloneDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map(item => cloneDeep(item)) as T;
  }

  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value)) {
      if (BLOCKED_KEYS.has(key)) {
        continue;
      }
      out[key] = cloneDeep(value[key]);
    }
    return out as T;
  }

  return value;
}

function mergeDeep(target: unknown, source: unknown, path: string): unknown {
  if (source === undefined) {
    return target;
  }

  if (Array.isArray(source)) {
    return cloneDeep(source);
  }

  if (typeof source === 'function') {
    return source;
  }

  if (!isPlainObject(source)) {
    return source;
  }

  const base: Record<string, unknown> = isPlainObject(target)
    ? cloneDeep(target)
    : {};

  for (const key of Object.keys(source)) {
    if (BLOCKED_KEYS.has(key)) {
      continue;
    }

    const srcVal = source[key];
    if (srcVal === undefined) {
      continue;
    }

    const nextPath = path ? `${path}.${key}` : key;
    if (nextPath === 'transport.sender') {
      base[key] = cloneDeep(srcVal);
      continue;
    }

    base[key] = mergeDeep(base[key], srcVal, nextPath);
  }

  return base;
}

/**
 * Resolve input público parcial em `PixelConfig` completo e isolado.
 *
 * Não muta `DEFAULT_PIXEL_CONFIG` nem o `settings` recebido.
 * Cada chamada produz uma árvore nova (sem refs mutáveis compartilhadas).
 */
export function resolvePixelConfig(settings: PixelSettings): PixelConfig {
  if (!isPlainObject(settings)) {
    throw new PixelConfigurationError(
      'Pixel settings must be a plain object.',
      'INVALID_SETTINGS'
    );
  }

  const merged = mergeDeep(
    cloneDeep(DEFAULT_PIXEL_CONFIG),
    settings,
    ''
  ) as PixelConfig;

  const rawPixelId = isPlainObject(settings.identity)
    ? settings.identity.pixelId
    : undefined;

  merged.identity = {
    ...merged.identity,
    pixelId: normalizePixelId(rawPixelId),
  };

  return merged;
}
